import { CoreApi, Product, ProductInfoComponent, ProductType } from '@inter-ikea-storageone/range';
import { BehaviorParameters } from 'rangeConfig/@types';
import {
  AutomaticGroupComponent,
  CommunicationSizeComponent,
  ComponentIds,
  Entity,
  ProductComponent,
  SnapSorterBuilder,
  SnapUtils,
  Transform,
  TransformUtils,
  Vector,
  WorldTransformComponent
} from '@inter-ikea-core/core';

const replaceFrame = ({
  coreApi,
  rootState,
  props,
  customFrame
}: BehaviorParameters & { customFrame?: Entity }) => {
  const frame = customFrame ?? coreApi.entities.findSelectedEntity();
  if (!frame) {
    return;
  }
  const products = rootState.products.inMarket;
  const newProduct = products.find(p => p.coreProductId === props.coreProductId);

  if (!newProduct?.coreProductId) {
    return;
  }

  if (newProduct?.type === ProductType.BILLY_FRAME_EXTENSION) {
    const extensionPlacementResult = coreApi.products.replace(frame.id, newProduct?.coreProductId);
    const newExtension = extensionPlacementResult?.replacedEntityResult?.placedEntity;
    if (extensionPlacementResult?.replacedEntityResult?.valid && newExtension && !customFrame) {
      coreApi.entities.select(newExtension);
    }
    return;
  }

  // Get doors and extensions on frame, regarldess of if automatic group or not
  const { doors, extensions } = getDoorsAndExtensions(frame, coreApi);

  // Get doors on extensions
  const extensionDoors = getExtensionDoors(extensions, coreApi);

  // Get shelves on frame
  const shelvesOnFrame = coreApi.entities.getDescendantsWithProductType(
    frame,
    ProductType.BILLY_SHELF
  );

  // Find all shelves on old frame with position
  const originalShelves = shelvesOnFrame.map(s => {
    const worldPosition = s.getComponent<WorldTransformComponent>(
      ComponentIds.WORLD_TRANSFORM_COMPONENT_ID
    )?.transform.position;
    const shelfDesign = s.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.design;

    const desiredWidth =
      s.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size
        .width ?? newProduct.communicatedDimensions.width;

    const desiredDepth = newProduct.communicatedDimensions.depth;
    const oldFrameDesign = frame.getComponent<ProductInfoComponent>(ProductInfoComponent.id)
      ?.design;

    const desiredDesign = newProduct.design;

    const shouldChangeShelfColor = shelfDesign === oldFrameDesign;

    const newShelfProduct = products.find(
      p =>
        p.type === ProductType.BILLY_SHELF &&
        p.communicatedDimensions.width === desiredWidth &&
        p.communicatedDimensions.depth === desiredDepth &&
        (shouldChangeShelfColor ? p.design === desiredDesign : p.design === shelfDesign)
    );

    return {
      entity: s,
      worldPosition,
      newShelfProduct
    };
  }) as {
    entity: Entity;
    worldPosition: Vector;
    newShelfProduct?: Product;
  }[];

  const shevlesWithoutReplacements = originalShelves.filter(s => !s.newShelfProduct);
  const missingShelfReplacements =
    shelvesOnFrame.length > 0 && shevlesWithoutReplacements.length > 0;

  // Delete all frames - will be readded later with updated design & depth
  originalShelves.forEach(s => coreApi.entities.delete(s.entity));

  // Delete extension if present - will be readded later with updated color & depth
  extensions?.forEach(extension => coreApi.entities.delete(extension));

  // Delete doors if present - will be readded later with updated color & depth
  doors?.forEach(door => coreApi.entities.delete(door.entity));

  const replacePlacementResult = coreApi.products.replace(frame.id, newProduct?.coreProductId);
  const newFrame = replacePlacementResult?.replacedEntityResult?.placedEntity;
  if (!newFrame) return;

  // Find all shelves on new frame and delete them (new ASM reapplies default shelves automatically)
  !missingShelfReplacements &&
    coreApi.entities
      .getDescendantsWithProductType(newFrame, ProductType.BILLY_SHELF)
      .forEach(s => coreApi.entities.delete(s));

  // Reapply extension with updated design & depth
  extensions &&
    handleExtension(coreApi, products, newProduct, newFrame, extensions, extensionDoors);

  // Reapply shelves with updated design & depth
  handleShelves(coreApi, originalShelves);

  // Reapply doors previously applied to the group. This must be done manually since we split the group when replacing the extension
  doors && handleDoorsInGroup(coreApi, doors);

  if (replacePlacementResult?.replacedEntityResult?.valid && newFrame && !customFrame) {
    coreApi.entities.select(newFrame);
  }
};

export default replaceFrame;

const handleExtension = (
  coreApi: CoreApi,
  products: Product[],
  newFrameProduct: Product,
  newFrameEntity: Entity,
  extensionsInGroup: Entity[],
  extensionDoors: { coreProductId: string; position: Vector }[]
) => {
  const extensionsInMarket = products.filter(p => p.type === ProductType.BILLY_FRAME_EXTENSION);
  const newFrameExtension = extensionsInMarket.find(extension => {
    return (
      newFrameProduct.communicatedDimensions.width === extension.communicatedDimensions?.width &&
      newFrameProduct.communicatedDimensions.depth === extension.communicatedDimensions?.depth &&
      newFrameProduct.design === extension.design
    );
  });
  const firstExtension = extensionsInGroup[0];
  const secondExtension = extensionsInGroup[1];
  if (firstExtension && newFrameExtension) {
    const placementResult = coreApi.products.addToTarget(
      newFrameExtension.coreProductId,
      newFrameEntity
    );
    const firtExtensionEntity = placementResult?.placedEntity;
    if (firtExtensionEntity && secondExtension) {
      coreApi.products.addToTarget(newFrameExtension.coreProductId, firtExtensionEntity);
    }
  }

  extensionDoors.forEach(door => {
    const doorEntity = coreApi.getServices().productManager.createEntity(door.coreProductId);

    if (doorEntity) {
      coreApi.placementService.snapToPosition(doorEntity, door.position, false);
    }
  });
};

const handleShelves = (
  coreApi: CoreApi,
  shelvesWithReplacements: {
    worldPosition: Vector;
    newShelfProduct?: Product;
  }[]
) => {
  // Iterate through all shelves on original frame and add with updated design & depth. Snap to world position for correct placement
  shelvesWithReplacements.forEach(s => {
    if (!s.newShelfProduct) return;
    const shelfEntity = coreApi
      .getServices()
      .productManager.createEntity(s.newShelfProduct.coreProductId);

    shelfEntity && coreApi.placementService.snapToPosition(shelfEntity, s.worldPosition, false);
  });
};

const handleDoorsInGroup = (
  coreApi: CoreApi,
  doorsInGroup: { coreProductId: string; worldPosition: Vector }[]
) => {
  doorsInGroup?.forEach(door => {
    const doorEntity = coreApi.getServices().productManager.createEntity(door.coreProductId);
    if (doorEntity) {
      // For some reason, coreApi.placementSerivce.snapToPosition doesn not work for entity groups
      // This is a work around to make sure the door are snaped as close to original position as possible
      const sceneManager = coreApi.getServices().sceneManager;
      const dinstanceSort = new SnapSorterBuilder().addDistanceCriterion().build();
      TransformUtils.setWorldTransform(doorEntity, new Transform(door.worldPosition));
      const snapCandidates = SnapUtils.getMatchingSnapsInScene(doorEntity, sceneManager);
      const sortedSnapCandidates = dinstanceSort.sortCandidates(snapCandidates);
      const sortedResults = coreApi
        .getServices()
        .validations.validateSnapCandidates(sortedSnapCandidates);
      const firstValidSnapCandidate = sortedResults.find(result => result.valid)?.snap;
      firstValidSnapCandidate && firstValidSnapCandidate.performMoveAndConnect();
    }
  });
};

const getDoorsAndExtensions = (frame: Entity, coreApi: CoreApi) => {
  const parent = coreApi.entities.getParent(frame);
  const automaticGroup = parent?.getComponent<AutomaticGroupComponent>(
    ComponentIds.AUTOMATIC_ENTITY_GROUP_COMPONENT_ID
  );

  const members = automaticGroup?.members;

  if (!members) {
    // No group, can use hierarchy as is
    const extension = coreApi.entities.findEntityInHierarchyWithOneOfProductTypes(frame, [
      ProductType.BILLY_FRAME_EXTENSION
    ]);

    const secondExtension =
      extension &&
      coreApi.entities.findDescendantWithOneOfProductTypes(extension, [
        ProductType.BILLY_FRAME_EXTENSION
      ]);

    const extensions = [extension, secondExtension].filter(Boolean) as Entity[];

    return { extensions };
  }

  // Group exist so we must iterate through all members to find doors and extensions
  const doorsInGroup = members?.filter(member =>
    member.hasComponent(ComponentIds.DOOR_COMPONENT_ID)
  );

  const extensions = members?.filter(
    member =>
      member.hasComponent(ComponentIds.FRAME_COMPONENT_ID) &&
      member.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType ===
        ProductType.BILLY_FRAME_EXTENSION
  );

  const doors = doorsInGroup
    ?.map(door => {
      const worldPosition = door.getComponent<WorldTransformComponent>(
        ComponentIds.WORLD_TRANSFORM_COMPONENT_ID
      )?.transform.position;
      const coreProductId = door.getComponent<ProductComponent>(ComponentIds.PRODUCT_COMPONENT_ID)
        ?.productRef;
      if (!worldPosition || !coreProductId) return null;
      return { entity: door, coreProductId, worldPosition };
    })
    .filter(Boolean) as { entity: Entity; coreProductId: string; worldPosition: Vector }[];

  return { doors, extensions };
};

const getExtensionDoors = (extensions: Entity[], coreApi: CoreApi) => {
  const extensionDoors =
    extensions
      ?.map(extension =>
        coreApi.entities.getDescendantsWithProductType(extension, ProductType.BILLY_DOOR_ASM)
      )
      .flat() ?? [];

  const extensionDoorsWithPosition = extensionDoors
    .map(entity => {
      const position = entity.getComponent<WorldTransformComponent>(
        ComponentIds.WORLD_TRANSFORM_COMPONENT_ID
      )?.transform.position;
      const coreProductId = entity.getComponent<ProductComponent>(ComponentIds.PRODUCT_COMPONENT_ID)
        ?.productRef;
      if (!position || !coreProductId) return null;
      return { coreProductId, position };
    })
    .filter(Boolean) as { coreProductId: string; position: Vector }[];

  return extensionDoorsWithPosition;
};
